import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { API, axios, formatDateUS } from "@/api";
import { BottomNav } from "@/components/shared/Navigation";
import { ActivityHeatmap } from "@/components/shared/ActivityHeatmap";
import FriendsBirthdaySection from "@/components/shared/FriendsBirthdaySection";
import TruckPassport from "@/components/trucks/TruckPassport";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Award, Sparkles, ChevronRight, Lightbulb, MessageSquare,
  Settings, LogOut, Loader2, BarChart3, History, Coins, Heart,
  Camera, Image as ImageIcon, MapPin, Navigation as NavIcon, Check, Calendar, Bookmark, PartyPopper,
  Leaf, Shield, Lock, Unlock, Users, Copy, Share2, ChevronDown, Mail, PlayCircle, Clock, Download, UserPlus, Gift,
} from "lucide-react";
import { toast } from "sonner";
import BadgeChaseCard from "@/components/shared/BadgeChaseCard";
import NotificationSettings from "@/components/shared/NotificationSettings";
import TooltipOverlay from "@/components/shared/TooltipOverlay";
import { useTheme, FRIENDS_MODE_ENABLED } from "@/contexts/ThemeContext";
import { trackPageView, trackError } from "@/lib/track";
import { useCopyBank } from "@/hooks/useCopyBank";
import TipOfTheDay from "@/components/shared/TipOfTheDay";
import MyContributions from "@/components/offers/MyContributions";
import TieredPunchCard from "@/components/punchcard/TieredPunchCard";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

// Tier id → tier number mapping for TieredPunchCard (1..9).
// Name/color retained for data-testid slugs and the tier label shown under each thumbnail.
const TIER_BADGES = [
  { id: "punch_tier_gray",     name: "Gray",     color: "#888888", tier: 1 },
  { id: "punch_tier_blue",     name: "Blue",     color: "#3a6ab0", tier: 2 },
  { id: "punch_tier_green",    name: "Green",    color: "#2e7d32", tier: 3 },
  { id: "punch_tier_purple",   name: "Purple",   color: "#6a1b9a", tier: 4 },
  { id: "punch_tier_red",      name: "Red",      color: "#c62828", tier: 5 },
  { id: "punch_tier_bronze",   name: "Bronze",   color: "#8d5524", tier: 6 },
  { id: "punch_tier_silver",   name: "Silver",   color: "#9e9e9e", tier: 7 },
  { id: "punch_tier_gold",     name: "Gold",     color: "#daa520", tier: 8 },
  { id: "punch_tier_platinum", name: "Platinum", color: "#c0c0e8", tier: 9 },
];

function LoyaltyBadgesSection() {
  const [badges, setBadges] = useState([]);
  useEffect(() => {
    axios.get(`${API}/gamification/summary`).then(res => {
      const tierBadges = (res.data?.badges || []).filter(b => b.badge_id?.startsWith("punch_tier_"));
      setBadges(tierBadges);
    }).catch(() => {});
  }, []);

  if (badges.length === 0) return null;

  const earnedIds = new Set(badges.map(b => b.badge_id));
  const earnedTiers = TIER_BADGES.filter(t => earnedIds.has(t.id));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4" data-testid="loyalty-badges-section">
      <p className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
        <Award className="w-4 h-4 text-[#F59E0B]" /> Loyalty Badges
      </p>
      <div className="grid grid-cols-3 gap-3" data-testid="loyalty-badges-grid">
        {earnedTiers.map(t => (
          <div
            key={t.id}
            className="flex flex-col items-center gap-1"
            data-testid={`loyalty-badge-${t.name.toLowerCase()}`}
          >
            <div className="w-full" style={{ maxWidth: 120 }}>
              <TieredPunchCard
                tier={t.tier}
                punches_earned={10}
                punches_required={10}
                business_name=""
                reward=""
              />
            </div>
            <p
              className="text-[10px] font-bold tracking-wide"
              style={{ color: t.color }}
            >
              {t.name}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function WrappedCalendarPreference() {
  const [pref, setPref] = useState("calendar");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/user/wrapped-preference`).then(res => {
      setPref(res.data.wrapped_calendar_preference || "calendar");
    }).catch(() => {});
  }, []);

  const save = async (val) => {
    setSaving(true);
    setPref(val);
    try {
      await axios.put(`${API}/user/wrapped-preference`, { wrapped_calendar_preference: val });
      toast.success("Wrapped calendar updated");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const options = [
    { id: "calendar", label: "Calendar Year", desc: "January to December" },
    { id: "birthday", label: "Birthday Year", desc: "From your birthday to your next birthday" },
    { id: "signup", label: "App Anniversary", desc: "From when you joined BirthdayDeaLight" },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4" data-testid="wrapped-calendar-settings">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-rose-500" />
        <p className="text-sm font-medium text-slate-700">Birthday Wrapped Calendar</p>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-slate-400 ml-auto" />}
      </div>
      <p className="text-xs text-slate-400 mb-3">This sets how we calculate your quarterly and annual Wrapped summaries.</p>
      <div className="space-y-2">
        {options.map(o => (
          <button
            key={o.id}
            onClick={() => save(o.id)}
            className={`w-full p-3 rounded-xl border text-left transition-all ${
              pref === o.id ? "border-amber-400 bg-amber-50" : "border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`wrapped-cal-${o.id}`}
          >
            <p className={`text-sm font-medium ${pref === o.id ? "text-amber-700" : "text-slate-600"}`}>{o.label}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{o.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const { user, logout, refreshUser } = useAuth();
  const { theme } = useTheme();
  const { getText } = useCopyBank();
  const { flags } = useFeatureFlags();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const [showDeleteStep, setShowDeleteStep] = useState(0);
  const [deleteReason, setDeleteReason] = useState("");
  const [showLocationEditor, setShowLocationEditor] = useState(false);
  // v1.0.8 — controlled signout dialog state. No asChild trigger, no
  // ref-forwarding edge cases on mobile WebKit. Tap → open. Confirm →
  // clear local state + navigate. Bulletproof.
  const [showSignOut, setShowSignOut] = useState(false);
  const performSignOut = () => {
    // Clear EVERYTHING locally first — not even one millisecond of waiting
    // for the network. Even if logout() throws, the user is signed out.
    try { localStorage.removeItem("token"); } catch {}
    try { localStorage.removeItem("access_token"); } catch {}
    try { Promise.resolve(logout()).catch(() => {}); } catch {}
    setShowSignOut(false);
    // Hard nav — skips React Router so any stale providers reset cleanly.
    window.location.replace("/onboarding");
  };
  const [editCity, setEditCity] = useState("");
  const [editState, setEditState] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);
  const [detectingGPS, setDetectingGPS] = useState(false);
  const [searchRadius, setSearchRadius] = useState(10);
  const [pendingReports, setPendingReports] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [dietaryPrefs, setDietaryPrefs] = useState({ no_restrictions: false, halal: false, vegan: false, vegetarian: false });
  const [showDietaryEditor, setShowDietaryEditor] = useState(false);
  const [savingDietary, setSavingDietary] = useState(false);
  const [tipsEnabled, setTipsEnabled] = useState(true);
  const [boycottCode, setBoycottCode] = useState("");
  const [showBoycottEditor, setShowBoycottEditor] = useState(false);
  const [savingBoycott, setSavingBoycott] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [savingDigest, setSavingDigest] = useState(false);
  const [sendingPreview, setSendingPreview] = useState(false);
  const [joiningBeta, setJoiningBeta] = useState(false);
  const [waitTolerance, setWaitTolerance] = useState(30);
  const [showReferral, setShowReferral] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [referralHistory, setReferralHistory] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const backCameraRef = useRef(null);

  useEffect(() => {
    trackPageView("profile");
    (async () => {
      try {
        const [statsRes, reportsRes, wishlistRes, dietaryRes, refRes, refHistRes, digestRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/admin/price-reports?status=pending`).catch(() => ({ data: [] })),
          axios.get(`${API}/wishlist/count`).catch(() => ({ data: { count: 0 } })),
          axios.get(`${API}/auth/dietary-preferences`).catch(() => ({ data: { halal: false, vegan: false, vegetarian: false } })),
          axios.get(`${API}/referral`).catch(() => ({ data: null })),
          axios.get(`${API}/referral/history`).catch(() => ({ data: { referrals: [] } })),
          axios.get(`${API}/digest/dietary/settings`).catch(() => ({ data: { dietary_digest_enabled: false } })),
        ]);
        setStats(statsRes.data);
        setPendingReports(reportsRes.data?.length || 0);
        setWishlistCount(wishlistRes.data?.count || 0);
        setReferralData(refRes.data);
        setReferralHistory(refHistRes.data);
        setDietaryPrefs(dietaryRes.data || { no_restrictions: false, halal: false, vegan: false, vegetarian: false });
        setDigestEnabled(digestRes.data?.dietary_digest_enabled || false);
        // Fetch wait tolerance
        try {
          const wtRes = await axios.get(`${API}/pivot/user-threshold`);
          setWaitTolerance(wtRes.data?.wait_tolerance_minutes ?? 30);
        } catch {}
      }
      catch (e) { console.error(e); trackError("profile_fetch", e); }
      finally { setLoading(false); }
    })();
  }, []);

  // Sync tip preference from user context
  useEffect(() => {
    setTipsEnabled(user?.tip_preferences?.tips_enabled !== false);
  }, [user?.tip_preferences?.tips_enabled]);

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setShowPhotoMenu(false);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/auth/profile-photo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profile photo updated!");
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error("Failed to upload photo"); trackError("photo_upload", e);
      console.error(e);
    } finally { setUploading(false); }
  };

  const openLocationEditor = () => {
    setEditCity(user?.city || "");
    setEditState(user?.state || "");
    setSearchRadius(user?.search_radius || 10);
    setShowLocationEditor(true);
  };

  const saveLocation = async () => {
    if (!editCity.trim()) return toast.error("City is required");
    setSavingLocation(true);
    try {
      await axios.put(`${API}/auth/location`, { city: editCity.trim(), state: editState.trim(), search_radius: searchRadius });
      toast.success(`Location updated to ${editCity.trim()}, ${editState.trim()}`);
      if (refreshUser) refreshUser();
      setShowLocationEditor(false);
    } catch (e) { toast.error("Failed to update location"); trackError("location_update", e); }
    finally { setSavingLocation(false); }
  };

  const detectGPS = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    setDetectingGPS(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`);
          const data = await res.json();
          const city = data.address?.city || data.address?.town || data.address?.village || "";
          const state = data.address?.state || "";
          if (city) { setEditCity(city); setEditState(state); toast.success(`Detected: ${city}, ${state}`); }
          else toast.error("Could not determine city from GPS");
        } catch { toast.error("Reverse geocoding failed"); }
        finally { setDetectingGPS(false); }
      },
      () => { toast.error("Location access denied"); setDetectingGPS(false); },
      { timeout: 10000 }
    );
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.pageBg }}><Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.primary }} /></div>;

  const saveDietaryPrefs = async () => {
    setSavingDietary(true);
    try {
      const res = await axios.put(`${API}/auth/dietary-preferences`, dietaryPrefs);
      setDietaryPrefs(res.data.dietary_preferences);
      toast.success("Dietary preferences saved!");
      if (refreshUser) refreshUser();
    } catch (e) { toast.error("Failed to save preferences"); }
    finally { setSavingDietary(false); }
  };

  const toggleTips = async () => {
    const newVal = !tipsEnabled;
    setTipsEnabled(newVal);
    try {
      await axios.put(`${API}/user/tip-preferences`, { tips_enabled: newVal });
    } catch { setTipsEnabled(!newVal); }
  };


  const toggleBoycott = async () => {
    if (user?.boycott_unlocked) {
      setSavingBoycott(true);
      try {
        await axios.post(`${API}/auth/boycott-lock`);
        toast.success("Boycott filter hidden");
        if (refreshUser) refreshUser();
      } catch (e) { toast.error("Failed to update"); }
      finally { setSavingBoycott(false); }
    } else {
      setShowBoycottEditor(true);
    }
  };

  const submitBoycottCode = async () => {
    if (!boycottCode.trim()) return toast.error("Enter your User ID");
    setSavingBoycott(true);
    try {
      await axios.post(`${API}/auth/boycott-unlock`, { code: boycottCode.trim() });
      toast.success("Boycott filter unlocked!");
      setShowBoycottEditor(false);
      setBoycottCode("");
      if (refreshUser) refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setSavingBoycott(false); }
  };

  const anyDietaryPref = dietaryPrefs.no_restrictions || dietaryPrefs.halal || dietaryPrefs.vegan || dietaryPrefs.vegetarian;

  const handleJoinBeta = async () => {
    setJoiningBeta(true);
    try {
      const res = await axios.post(`${API}/gamification/beta/join`);
      toast.success(res.data.founding_tester ? "Welcome, Founding Tester!" : "Welcome to Beta!");
      if (refreshUser) refreshUser();
    } catch (e) { toast.error("Failed to join beta"); }
    finally { setJoiningBeta(false); }
  };

  const handleLeaveBeta = async () => {
    setJoiningBeta(true);
    try {
      await axios.post(`${API}/gamification/beta/leave`);
      toast.success("Beta status removed. Your badges are kept.");
      if (refreshUser) refreshUser();
    } catch (e) { toast.error("Failed to leave beta"); }
    finally { setJoiningBeta(false); }
  };

  const menuItems = [
    { icon: Heart, label: "My Favorites", desc: "Businesses you personally like — build itineraries, track claims", path: "/my-deals", color: "text-pink-500 bg-pink-50" },
    { icon: Bookmark, label: "My Wishlist", desc: "Shareable birthday deal registry for friends & family", path: "/wishlist", color: "text-violet-500 bg-violet-50", badge: wishlistCount },
    { icon: Coins, label: "How to Earn Points", path: "/earn-points", color: "text-amber-500 bg-amber-50" },
    { icon: BarChart3, label: "My Analytics", path: "/my-analytics", color: "text-emerald-500 bg-emerald-50" },
    { icon: Sparkles, label: "My Decision", path: "/my-decision", color: "text-violet-500 bg-violet-50", flag: "full_decision_engine" },
    { icon: Calendar, label: "My Bday Itinerary", path: "/my-itinerary", color: "text-pink-500 bg-pink-50", flag: "full_itinerary_builder" },
    { icon: PartyPopper, label: "Birthday Summary", path: `/birthday-summary/${new Date().getFullYear()}`, color: "text-purple-500 bg-purple-50" },
    { icon: Sparkles, label: "Birthday Wrapped", desc: "Your personalized birthday deal recap", path: "/birthday-wrapped", color: "text-rose-500 bg-rose-50", flag: "birthday_wrapped_full" },
    { icon: History, label: "Wrapped History", desc: "View all your past Wrapped reports", path: "/wrapped-history", color: "text-rose-500 bg-rose-50", flag: "birthday_wrapped_full" },
    { icon: History, label: "Verification History", path: "/verification-history", color: "text-sky-500 bg-sky-50" },
    { icon: Coins, label: "Points History", path: "/points-history", color: "text-amber-500 bg-amber-50" },
    { icon: MessageSquare, label: "My Submissions", path: "/my-submissions", color: "text-teal-500 bg-teal-50" },
    { icon: Lightbulb, label: "Tips & Guide", path: "/tips", color: "text-green-500 bg-green-50" },
    { icon: MessageSquare, label: "Send Feedback", path: "/feedback", color: "text-pink-500 bg-pink-50" },
    { icon: Settings, label: "Admin Dashboard", path: "/admin", color: "text-indigo-500 bg-indigo-50" },
  ].filter((m) => !m.flag || flags[m.flag]);

  const profilePhotoUrl = user?.profile_photo_url;

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: theme.pageBg }} data-testid="profile-page">
      <TipOfTheDay pageKey="user_profile" />
      <div className="text-white p-6" style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})` }}>
        <div className="flex items-center gap-4">
          {/* Profile Photo with Camera Overlay */}
          <div className="relative" data-testid="profile-photo-container">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold overflow-hidden">
              {profilePhotoUrl ? (
                <img src={profilePhotoUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                user?.first_name?.charAt(0) || "?"
              )}
            </div>
            <button
              onClick={() => setShowPhotoMenu(!showPhotoMenu)}
              className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center"
              data-testid="photo-menu-btn"
            >
              {uploading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-500" />
              ) : (
                <Camera className="w-3.5 h-3.5 text-sky-500" />
              )}
            </button>

            {/* Photo Menu */}
            {showPhotoMenu && (
              <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-10 w-44" data-testid="photo-menu">
                <p className="px-4 pt-2.5 pb-1 text-[10px] text-slate-400 font-medium" data-testid="photo-upload-prompt">{getText("photo_verification")}</p>
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="w-full px-4 py-3 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5 transition-colors"
                  data-testid="take-selfie-btn"
                >
                  <Camera className="w-4 h-4 text-sky-500" /> Take Selfie
                </button>
                <button
                  onClick={() => backCameraRef.current?.click()}
                  className="w-full px-4 py-3 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5 border-t border-slate-100 transition-colors"
                  data-testid="take-photo-btn"
                >
                  <Camera className="w-4 h-4 text-emerald-500" /> Take Photo
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-4 py-3 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5 border-t border-slate-100 transition-colors"
                  data-testid="choose-photo-btn"
                >
                  <ImageIcon className="w-4 h-4 text-indigo-500" /> Choose Photo
                </button>
              </div>
            )}

            {/* Hidden file inputs */}
            <input ref={cameraInputRef} type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => handlePhotoUpload(e.target.files?.[0])} />
            <input ref={backCameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handlePhotoUpload(e.target.files?.[0])} />
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e.target.files?.[0])} />
          </div>

          <div>
            <h1 className="text-xl font-bold">{user?.first_name} {user?.last_initial}.</h1>
            <p className="text-sky-100 text-sm">{user?.email}</p>
            <p className="text-sky-200 text-xs mt-0.5">{user?.city}, {user?.state} · Birthday: {formatDateUS(user?.birthday)} <Lock className="w-3 h-3 inline text-sky-300/50" /></p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          <div className="bg-white/10 rounded-xl p-3 text-center"><Award className="w-5 h-5 mx-auto mb-1" /><span className="text-xs text-sky-200 block">Level</span><span className="text-sm font-bold">{stats?.level || user?.level}</span></div>
          <div className="bg-white/10 rounded-xl p-3 text-center"><Sparkles className="w-5 h-5 mx-auto mb-1" /><span className="text-xs text-sky-200 block">Score</span><span className="text-sm font-bold">{stats?.crowdsourcing_score || user?.crowdsourcing_score || 0}</span></div>
          <div className="bg-white/10 rounded-xl p-3 text-center"><Award className="w-5 h-5 mx-auto mb-1" /><span className="text-xs text-sky-200 block">Verified</span><span className="text-sm font-bold">{stats?.user_verifications || 0}</span></div>
        </div>
      </div>

      <div className="px-4 mt-4 space-y-2">
        {/* Profile Completion Prompt — shown when profile is incomplete */}
        {(!user?.city || !user?.birthday || !profilePhotoUrl) && (
          <div className="bg-gradient-to-r from-sky-50 to-indigo-50 rounded-xl p-4 border border-sky-200" data-testid="profile-completion-prompt">
            <p className="text-sm font-semibold text-sky-800">{getText("complete_profile")}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {!user?.city && <span className="px-2 py-0.5 text-[10px] font-medium bg-sky-100 text-sky-600 rounded-full">Add city</span>}
              {!user?.birthday && <span className="px-2 py-0.5 text-[10px] font-medium bg-sky-100 text-sky-600 rounded-full">Add birthday</span>}
              {!profilePhotoUrl && <span className="px-2 py-0.5 text-[10px] font-medium bg-sky-100 text-sky-600 rounded-full">Add photo</span>}
            </div>
          </div>
        )}

        {user?.uid && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between"><span className="text-xs text-slate-400 font-medium">UID</span><button onClick={() => { navigator.clipboard.writeText(user.uid).then(() => toast.success("UID copied!")).catch(() => toast.success("UID copied!")); }} className="flex items-center gap-1.5 group" data-testid="copy-uid-btn"><span className="text-xs font-mono text-slate-600">{user.uid}</span><Copy className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 transition" /></button></div>
          </div>
        )}

        {/* Badge Chase — top 3 closest badges */}
        <BadgeChaseCard />

        {/* Loyalty Badges (Tier) */}
        <LoyaltyBadgesSection />

        {/* Location Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-testid="location-settings">
          <button
            onClick={() => {
              if (showLocationEditor) {
                setShowLocationEditor(false);
              } else {
                openLocationEditor();
              }
            }}
            className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition"
            data-testid="location-settings-btn"
          >
            <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-teal-500" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-slate-700">My Location</p>
              <p className="text-xs text-slate-400">{user?.city}{user?.state ? `, ${user.state}` : ""}</p>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showLocationEditor ? "rotate-180" : ""}`} />
          </button>

          {showLocationEditor && (
            <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3">
              <p className="text-xs text-slate-400">Visiting another city? Update your location for local deals.</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">City</label>
                  <input
                    type="text" value={editCity} onChange={(e) => setEditCity(e.target.value)}
                    placeholder="e.g. Los Angeles"
                    className="w-full px-3 py-2 bg-slate-50 rounded-lg text-sm outline-none border border-slate-200 focus:border-teal-400 mt-0.5"
                    data-testid="location-city-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">State</label>
                  <input
                    type="text" value={editState} onChange={(e) => setEditState(e.target.value)}
                    placeholder="e.g. California"
                    className="w-full px-3 py-2 bg-slate-50 rounded-lg text-sm outline-none border border-slate-200 focus:border-teal-400 mt-0.5"
                    data-testid="location-state-input"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={detectGPS}
                  disabled={detectingGPS}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200 transition disabled:opacity-50"
                  data-testid="detect-gps-btn"
                >
                  {detectingGPS ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <NavIcon className="w-3.5 h-3.5" />}
                  Use GPS
                </button>
                <button
                  onClick={saveLocation}
                  disabled={savingLocation}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-teal-500 text-white rounded-lg text-xs font-medium hover:bg-teal-600 transition disabled:opacity-50"
                  data-testid="save-location-btn"
                >
                  {savingLocation ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Save
                </button>
              </div>
              {/* Search Radius Slider — tick labels are absolutely positioned at
                  their actual % offset so the scroll dot always lines up with
                  the printed value. The slider value range is 1..20, so the
                  thumb at value N sits at (N-1)/19 of the track. */}
              <div className="pt-2">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Search Radius</label>
                  <span className="text-xs font-bold text-teal-600" data-testid="radius-value">{searchRadius} mi</span>
                </div>
                <input
                  type="range" min={1} max={20} step={1} value={searchRadius}
                  onChange={(e) => setSearchRadius(parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-teal-500"
                  data-testid="radius-slider"
                />
                <div className="relative h-4 mt-0.5">
                  {[1, 5, 10, 15, 20].map((v) => (
                    <span
                      key={v}
                      className="absolute text-[9px] text-slate-400 -translate-x-1/2"
                      style={{ left: `${((v - 1) / 19) * 100}%` }}
                      data-testid={`radius-tick-${v}`}
                    >
                      {v === 1 ? "1 mi" : v === 20 ? "20 mi" : v}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Activity Heatmap */}
        <ActivityHeatmap userId={user?.id} />

        {/* My Contributions — v5.7.0 */}
        <MyContributions />

        {/* Friends' Birthday Tracking */}
        <FriendsBirthdaySection />

        {/* Birthday Buddy — Full Page Access */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4" data-testid="birthday-buddy-nav-section">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <Users className="w-5 h-5 text-amber-500" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-700">Birthday Buddy</p>
              <p className="text-[10px] text-slate-400">Save friends, get reminders, plan their birthday</p>
            </div>
            {!FRIENDS_MODE_ENABLED && (
              <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full font-medium" data-testid="friends-coming-soon">Coming Soon</span>
            )}
          </div>
          {FRIENDS_MODE_ENABLED ? (
            <div className="space-y-2">
              <button onClick={() => navigate("/friends/birthdays")} className="w-full py-2.5 bg-amber-50 text-amber-600 rounded-lg text-sm font-medium flex items-center justify-center gap-2" data-testid="nav-birthday-buddy">
                <UserPlus className="w-4 h-4" /> Open Birthday Buddy
              </button>
              <button onClick={() => navigate("/friends/referrals")} className="w-full py-2.5 bg-slate-50 text-slate-600 rounded-lg text-sm font-medium flex items-center justify-center gap-2" data-testid="nav-my-referrals">
                <Gift className="w-4 h-4" /> My Referrals
              </button>
            </div>
          ) : (
            <p className="text-xs text-slate-400 ml-[52px]">Birthday Buddy, referral rewards, and friend planning tools are coming soon.</p>
          )}
        </div>

        {/* Truck Passport — v1.0 hidden behind truck_features master flag */}
        {flags.truck_features && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden p-4" data-testid="truck-passport-section">
            <TruckPassport />
          </div>
        )}

        {/* Birthday — Locked Display */}
        {user?.birthday && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4" data-testid="birthday-locked-display">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <Lock className="w-5 h-5 text-amber-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700">Your Birthday</p>
                <p className="text-xs text-slate-500">{formatDateUS(user.birthday)}</p>
              </div>
              <Lock className="w-4 h-4 text-slate-300" />
            </div>
            <p className="text-[10px] text-slate-400 mt-2 ml-[52px]">Your birthday is locked. Contact hello@birthdaydealight.com if there is an error.</p>
          </div>
        )}

        {/* Tips of the Day */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4" data-testid="tips-settings">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <span className="text-lg">💡</span>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">Tips of the Day</p>
                <p className="text-[10px] text-slate-400">{tipsEnabled ? "Showing helpful tips" : "Tips disabled"}</p>
              </div>
            </div>
            <button
              onClick={toggleTips}
              className={`relative w-11 h-6 rounded-full transition-colors ${tipsEnabled ? "bg-amber-500" : "bg-slate-200"}`}
              data-testid="tips-toggle"
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${tipsEnabled ? "left-6" : "left-1"}`} />
            </button>
          </div>
        </div>

        {/* Dietary Preferences */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-testid="dietary-preferences-settings">
          <button
            onClick={() => setShowDietaryEditor(!showDietaryEditor)}
            className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition"
            data-testid="dietary-preferences-btn"
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Leaf className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-slate-700">Dietary Preferences</p>
              <p className="text-xs text-slate-400">{anyDietaryPref ? [dietaryPrefs.no_restrictions && "No restrictions", dietaryPrefs.halal && "Halal", dietaryPrefs.vegan && "Vegan", dietaryPrefs.vegetarian && "Vegetarian"].filter(Boolean).join(", ") : "None set"}</p>
            </div>
            <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${showDietaryEditor ? "rotate-90" : ""}`} />
          </button>

          {showDietaryEditor && (
            <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3">
              <p className="text-xs text-slate-400">Your default dietary filters. These are auto-applied each time you open the app.</p>
              {[
                { key: "no_restrictions", label: "No restrictions", desc: "I have no dietary restrictions" },
                { key: "halal", label: "Halal", desc: "Show Halal Certified or Friendly places" },
                { key: "vegan", label: "Vegan", desc: "Show Vegan or Vegan-Friendly places" },
                { key: "vegetarian", label: "Vegetarian", desc: "Show Vegetarian-Friendly places" },
              ].map(({ key, label, desc }) => (
                <button
                  key={key}
                  // "No restrictions" is mutually exclusive with the
                  // specific-need toggles. Toggling it clears the
                  // others; toggling any other clears it. This keeps
                  // the saved state honest (you can't simultaneously
                  // claim "no restrictions" AND "I need halal").
                  onClick={() => setDietaryPrefs(p => {
                    if (key === "no_restrictions") {
                      const next = !p.no_restrictions;
                      return next
                        ? { no_restrictions: true, halal: false, vegan: false, vegetarian: false }
                        : { ...p, no_restrictions: false };
                    }
                    return { ...p, [key]: !p[key], no_restrictions: false };
                  })}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition ${dietaryPrefs[key] ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}
                  data-testid={`dietary-pref-${key}`}
                >
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition ${dietaryPrefs[key] ? "border-emerald-500 bg-emerald-500" : "border-slate-300"}`}>
                    {dietaryPrefs[key] && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="text-left flex-1">
                    <p className={`text-sm font-medium ${dietaryPrefs[key] ? "text-emerald-700" : "text-slate-600"}`}>{label}</p>
                    <p className="text-[10px] text-slate-400">{desc}</p>
                  </div>
                </button>
              ))}
              {/* Coming-soon placeholder — non-interactive label, signals
                  that more dietary needs (gluten-free, kosher, nut-free,
                  etc.) are on the roadmap. */}
              <div
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/60 cursor-default select-none"
                data-testid="dietary-coming-soon"
                aria-disabled="true"
              >
                <div className="w-5 h-5 rounded-md border-2 border-slate-200 flex items-center justify-center">
                  <span className="text-[9px] text-slate-300">+</span>
                </div>
                <div className="text-left flex-1">
                  <p className="text-sm font-medium text-slate-400">Coming soon — other dietary needs</p>
                  <p className="text-[10px] text-slate-300">Gluten-free, kosher, nut-free and more on the way</p>
                </div>
              </div>
              <button
                onClick={saveDietaryPrefs}
                disabled={savingDietary}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-emerald-500 text-white rounded-xl text-xs font-medium hover:bg-emerald-600 transition disabled:opacity-50"
                data-testid="save-dietary-prefs-btn"
              >
                {savingDietary ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Save Dietary Defaults
              </button>
            </div>
          )}
        </div>

        {/* Weekly Dietary Digest */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-testid="dietary-digest-settings">
          <div className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
              <Mail className="w-5 h-5 text-violet-500" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-700">Weekly Dietary Digest</p>
              <p className="text-xs text-slate-400">Get new matches emailed weekly</p>
            </div>
            <button
              onClick={async () => {
                const newVal = !digestEnabled;
                setSavingDigest(true);
                try {
                  await axios.put(`${API}/digest/dietary/settings`, { dietary_digest_enabled: newVal });
                  setDigestEnabled(newVal);
                  toast.success(newVal ? "Digest emails enabled!" : "Digest emails disabled");
                } catch { toast.error("Failed to update"); }
                finally { setSavingDigest(false); }
              }}
              disabled={savingDigest}
              className={`w-11 h-6 rounded-full transition-colors relative ${digestEnabled ? "bg-violet-500" : "bg-slate-200"}`}
              data-testid="digest-toggle"
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-all ${digestEnabled ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
          {digestEnabled && (
            <div className="px-4 pb-4 border-t border-slate-100 pt-3">
              <p className="text-xs text-slate-400 mb-3">We'll email you weekly with new birthday deals matching your dietary preferences.</p>
              <button
                onClick={async () => {
                  setSendingPreview(true);
                  try {
                    const res = await axios.post(`${API}/digest/dietary/send-preview`);
                    if (res.data?.sent) {
                      toast.success(res.data.message || "Preview sent!");
                    } else {
                      toast.info(res.data?.message || "No matching deals found");
                    }
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed to send preview");
                  }
                  finally { setSendingPreview(false); }
                }}
                disabled={sendingPreview || !anyDietaryPref}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-violet-500 text-white rounded-xl text-xs font-medium hover:bg-violet-600 transition disabled:opacity-50"
                data-testid="send-digest-preview"
              >
                {sendingPreview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                Send me a preview now
              </button>
              {!anyDietaryPref && (
                <p className="text-[10px] text-amber-500 mt-2 text-center">Set dietary preferences above first</p>
              )}
            </div>
          )}
        </div>

        {/* Wait Tolerance */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-testid="wait-tolerance-settings">
          <div className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700">My Wait Tolerance</p>
                <p className="text-xs text-slate-400">The longest line you&apos;re willing to wait in</p>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed mt-1" data-testid="wait-tolerance-explainer">
              Set the longest reported wait time you&apos;ll tolerate at a venue.
              When you tap a deal whose current wait exceeds this, we&apos;ll
              automatically surface similar nearby spots with shorter
              waits so you don&apos;t burn your birthday standing in line.
              Choose <strong>No limit</strong> to disable auto-suggestions.
            </p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {[5, 10, 15, 20, 30, 45, 60, 0].map((mins) => (
                <button
                  key={mins}
                  onClick={async () => {
                    setWaitTolerance(mins);
                    try {
                      await axios.put(`${API}/pivot/user-threshold`, { minutes: mins });
                      toast.success(mins === 0 ? "No wait limit set" : `Wait tolerance set to ${mins} min`);
                    } catch { toast.error("Failed to save"); }
                  }}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition ${
                    waitTolerance === mins
                      ? "bg-orange-500 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`wait-tolerance-${mins}`}
                >
                  {mins === 0 ? "No limit" : `${mins} min`}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Push Notifications */}
        <NotificationSettings />

        {/* Birthday Wrapped Calendar Preference — gated behind birthday_wrapped_full */}
        {flags.birthday_wrapped_full && <WrappedCalendarPreference />}

        {/* Boycott Unlock */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-testid="boycott-settings">
          <button
            onClick={toggleBoycott}
            className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition"
            data-testid="boycott-settings-btn"
          >
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
              {user?.boycott_unlocked ? <Unlock className="w-5 h-5 text-slate-600" /> : <Lock className="w-5 h-5 text-slate-400" />}
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-slate-700">Boycott Filter</p>
              <p className="text-xs text-slate-400">{user?.boycott_unlocked ? "Unlocked — tap to hide" : "Hidden — enter UID to unlock"}</p>
            </div>
            {savingBoycott && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          </button>

          {showBoycottEditor && !user?.boycott_unlocked && (
            <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3">
              <p className="text-xs text-slate-400">Enter your User ID to unlock the boycott filter in the filter sheet.</p>
              <input
                type="text"
                value={boycottCode}
                onChange={(e) => setBoycottCode(e.target.value)}
                placeholder="Enter your UID"
                className="w-full px-3 py-2 bg-slate-50 rounded-lg text-sm outline-none border border-slate-200 focus:border-slate-400"
                data-testid="boycott-code-input"
              />
              <button
                onClick={submitBoycottCode}
                disabled={savingBoycott}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-slate-700 text-white rounded-xl text-xs font-medium hover:bg-slate-800 transition disabled:opacity-50"
                data-testid="boycott-unlock-btn"
              >
                {savingBoycott ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
                Unlock
              </button>
            </div>
          )}
        </div>

        {/* Beta Program */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-testid="beta-program-settings">
          {user?.beta_tester ? (
            /* --- ACTIVE BETA STATE — subtle, status-focused --- */
            <div className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-purple-700">Beta Tester Active</p>
                    {user?.is_founding_tester && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-700">FOUNDING</span>}
                  </div>
                  <p className="text-[11px] text-slate-400">2x points on all events · Early feature access</p>
                </div>
              </div>
              {/* iter317 — Real button styling for destructive action.
                  Previously this was a 10px slate-400 <p> with no border,
                  invisible against the white card. Now it's an outlined
                  button with high-contrast slate text + dark border so
                  users can actually see and click it. */}
              <button
                onClick={handleLeaveBeta}
                disabled={joiningBeta}
                className="w-full mt-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-600 text-xs font-medium hover:bg-slate-50 hover:border-slate-400 hover:text-slate-800 transition disabled:opacity-50"
                data-testid="leave-beta-btn"
              >
                {joiningBeta ? "Processing..." : "Leave Beta Program"}
              </button>
            </div>
          ) : (
            /* --- JOIN STATE — prominent incentive card --- */
            <div className="p-4">
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 mb-3 border border-purple-100">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  <p className="text-sm font-bold text-purple-700">Join the Beta Program</p>
                </div>
                <ul className="text-xs text-slate-600 space-y-1.5 ml-1">
                  <li className="flex items-start gap-1.5"><span className="text-purple-400 mt-0.5">&#x2022;</span>First access to new features before everyone</li>
                  <li className="flex items-start gap-1.5"><span className="text-purple-400 mt-0.5">&#x2022;</span><strong>2x points</strong> on every action while testing</li>
                  <li className="flex items-start gap-1.5"><span className="text-purple-400 mt-0.5">&#x2022;</span>Permanent <strong>Beta Tester</strong> badge on your profile</li>
                  <li className="flex items-start gap-1.5"><span className="text-purple-400 mt-0.5">&#x2022;</span>Help shape what millions of users will experience</li>
                </ul>
              </div>
              <button
                onClick={handleJoinBeta}
                disabled={joiningBeta}
                className="w-full py-2.5 rounded-xl bg-purple-600 text-white text-xs font-bold hover:bg-purple-700 transition disabled:opacity-50"
                data-testid="join-beta-btn"
              >
                {joiningBeta ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Join Beta Program"}
              </button>
            </div>
          )}
        </div>

        {/* Refer a Friend — Accordion */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-testid="refer-friend-settings">
          <button
            onClick={() => setShowReferral(!showReferral)}
            className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition"
            data-testid="refer-friend-toggle"
          >
            <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center">
              <Users className="w-5 h-5 text-rose-500" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-slate-700">Refer a Friend</p>
              <p className="text-xs text-slate-400">{referralData?.referral_count || 0} referred — 50 pts each</p>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showReferral ? "rotate-180" : ""}`} />
          </button>

          {showReferral && (
            <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3">
              {/* Referral code + copy */}
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2.5 flex items-center justify-between">
                  <code className="text-sm font-mono font-bold text-rose-600" data-testid="settings-referral-code">{referralData?.referral_code || "..."}</code>
                  <button
                    onClick={() => {
                      if (referralData?.referral_code) {
                        navigator.clipboard.writeText(referralData.referral_code);
                        toast.success("Referral code copied!");
                      }
                    }}
                    className="p-1 hover:bg-rose-100 rounded"
                    data-testid="settings-copy-referral"
                  >
                    <Copy className="w-4 h-4 text-rose-400" />
                  </button>
                </div>
              </div>

              {/* Share button */}
              <button
                onClick={() => {
                  const msg = referralData?.referral_code
                    ? `Join me on BirthdayDeaLight! Use my code ${referralData.referral_code} when you sign up. https://birthdaydealight.com/onboarding?ref=${referralData.referral_code}`
                    : "";
                  if (navigator.share) {
                    navigator.share({ text: msg }).catch(() => {});
                  } else {
                    navigator.clipboard.writeText(msg);
                    toast.success("Referral link copied!");
                  }
                }}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-rose-500 text-white rounded-xl text-xs font-bold hover:bg-rose-600 transition"
                data-testid="settings-share-referral"
              >
                <Share2 className="w-3.5 h-3.5" /> Share Referral Link
              </button>

              {referralData?.next_milestone && (
                <p className="text-xs text-rose-400 flex items-center gap-1">
                  {referralData.next_milestone.count - (referralData?.referral_count || 0)} more for +{referralData.next_milestone.bonus} bonus!
                </p>
              )}

              {/* Referral History */}
              <div className="border-t border-dashed border-slate-200 pt-3 mt-1">
                <p className="text-xs font-semibold text-slate-600 mb-2">Referral History</p>
                {referralHistory?.referrals?.length > 0 ? (
                  <div className="space-y-2">
                    {referralHistory.referrals.map((ref, i) => (
                      <div key={i} className="flex items-center gap-2.5 bg-slate-50 rounded-lg px-3 py-2" data-testid={`settings-referral-entry-${i}`}>
                        <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-500">
                          {(ref.name || ref.masked_email || "?")[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-700 truncate">{ref.masked_email}</p>
                          <p className="text-[10px] text-slate-400">{ref.date ? new Date(ref.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}</p>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200">Signed Up</span>
                        <span className="text-[10px] font-bold text-emerald-600">+{ref.points_earned}pts</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 text-center py-3">No referrals yet. Share your link to get started.</p>
                )}
              </div>
            </div>
          )}
        </div>


        {menuItems.map((item) => (
          <button key={item.path + item.label} onClick={() => navigate(item.path)} className="w-full bg-white rounded-xl p-4 shadow-sm border border-slate-100 flex items-center gap-3 hover:bg-slate-50 transition relative" data-testid={`menu-${item.label.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className={`w-10 h-10 rounded-xl ${item.color} flex items-center justify-center`}><item.icon className="w-5 h-5" /></div>
            <div className="flex-1 text-left min-w-0">
              <span className="text-sm font-medium text-slate-700">{item.label}</span>
              {item.desc && <p className="text-[11px] text-slate-400 leading-tight mt-0.5 truncate">{item.desc}</p>}
            </div>
            {item.badge > 0 && (
              <span className="min-w-5 h-5 px-1.5 rounded-full bg-violet-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`badge-${item.path.slice(1)}`}>{item.badge}</span>
            )}
            {item.path === "/admin" && pendingReports > 0 && (
              <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse" data-testid="admin-report-badge">{pendingReports}</span>
            )}
            <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
          </button>
        ))}
      </div>

      <div className="px-4 mt-4 space-y-2">
        <button
          onClick={async () => {
            try {
              await axios.post(`${API}/auth/tour-reset`);
              toast.success("Tour will restart — heading home");
              navigate("/");
              setTimeout(() => window.location.reload(), 300);
            } catch { toast.error("Could not reset tour"); }
          }}
          className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-slate-400 hover:text-slate-600 transition"
          data-testid="replay-tour-btn"
        >
          <PlayCircle className="w-3.5 h-3.5" />
          <span>Replay app tour</span>
        </button>
        <button
          onClick={() => {
            const { triggerInstallPrompt } = require("@/components/shared/InstallPromptBanner");
            if (triggerInstallPrompt) {
              triggerInstallPrompt.prompt();
              triggerInstallPrompt.userChoice.then(({ outcome }) => {
                if (outcome === "accepted") {
                  localStorage.setItem("install_prompt_dismissed", "true");
                  toast.success("Installing BirthdayDeaLight!");
                }
              });
            } else if (window.matchMedia("(display-mode: standalone)").matches) {
              toast.success("Already installed!");
            } else {
              toast("To install: tap your browser's menu, then 'Add to Home Screen'", { duration: 5000 });
            }
          }}
          className="w-full bg-white rounded-xl p-4 shadow-sm border border-amber-100 flex items-center gap-3 hover:bg-amber-50 transition"
          data-testid="install-app-btn"
        >
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><Download className="w-5 h-5 text-amber-500" /></div>
          <div className="text-left">
            <span className="text-sm font-medium text-slate-700 block">Add to Home Screen</span>
            <span className="text-[10px] text-slate-400">Install BirthdayDeaLight for the best experience</span>
          </div>
        </button>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between"><span className="text-xs text-slate-400">App Version</span><span className="text-xs font-mono text-slate-500">{stats?.app_version || "1.0.0"}</span></div>
        </div>
        <button
          type="button"
          onClick={() => setShowSignOut(true)}
          onTouchEnd={(e) => {
            // Mobile WebKit fallback — touchend fires reliably even when click
            // is suppressed by an ancestor (BottomNav fixed overlay edge cases,
            // PWA standalone mode, in-app browsers).
            e.preventDefault();
            setShowSignOut(true);
          }}
          className="w-full bg-white rounded-xl p-4 shadow-sm border border-red-100 flex items-center gap-3 hover:bg-red-50 active:bg-red-100 transition min-h-[56px] touch-manipulation relative z-10"
          data-testid="logout-btn"
        >
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
            <LogOut className="w-5 h-5 text-red-500" />
          </div>
          <span className="text-sm font-medium text-red-600">Sign Out</span>
        </button>
        <AlertDialog open={showSignOut} onOpenChange={setShowSignOut}>
          <AlertDialogContent data-testid="logout-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Sign out?</AlertDialogTitle>
              <AlertDialogDescription>
                You'll need to sign in again to view your birthday deals, points, and saved offers.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="logout-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="logout-confirm-btn"
                onClick={performSignOut}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
              >
                Sign Out
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ── Delete Account + Privacy ── */}
        <div className="mt-6 pt-6 border-t border-dashed border-slate-200 space-y-3">
          <div className="flex items-center gap-4 px-1" data-testid="profile-legal-links">
            <a href="/privacy" className="text-[11px] text-slate-400 hover:text-slate-600 underline min-h-[44px] flex items-center" data-testid="profile-privacy-link">Privacy Policy</a>
            <a href="/terms" className="text-[11px] text-slate-400 hover:text-slate-600 underline min-h-[44px] flex items-center" data-testid="profile-terms-link">Terms of Service</a>
          </div>
          <button
            onClick={() => setShowDeleteStep(1)}
            className="w-full text-left text-xs text-red-400 hover:text-red-600 transition px-1"
            data-testid="delete-account-btn"
          >
            Delete my account
          </button>
          <p className="text-[10px] text-slate-300 px-1" data-testid="privacy-note">
            To request a copy of your data, email hello@birthdaydealight.com
          </p>
        </div>
      </div>

      {/* Delete Account Modal — Step 1 */}
      {showDeleteStep === 1 && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="delete-modal-step1">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-base font-bold text-slate-800 mb-2">Delete your account?</h3>
            <p className="text-xs text-slate-500 mb-4">
              This will permanently delete your account, points, badges, and all personal data after a 30-day grace period. This cannot be undone.
            </p>
            <label className="block text-xs text-slate-400 mb-1">Tell us why (optional)</label>
            <select
              value={deleteReason}
              onChange={e => setDeleteReason(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600 mb-4"
              data-testid="delete-reason-select"
            >
              <option value="">Select a reason...</option>
              <option value="Changed my mind">Changed my mind</option>
              <option value="Privacy concerns">Privacy concerns</option>
              <option value="Too many notifications">Too many notifications</option>
              <option value="App not useful">App not useful</option>
              <option value="Other">Other</option>
            </select>
            <div className="flex gap-2">
              <button onClick={() => { setShowDeleteStep(0); setDeleteReason(""); }} className="flex-1 px-4 py-2.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition" data-testid="delete-cancel-btn">Cancel</button>
              <button onClick={() => setShowDeleteStep(2)} className="flex-1 px-4 py-2.5 text-xs font-medium text-white bg-red-500 rounded-xl hover:bg-red-600 transition" data-testid="delete-continue-btn">Continue</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal — Step 2 */}
      {showDeleteStep === 2 && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="delete-modal-step2">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-base font-bold text-red-600 mb-2">Are you absolutely sure?</h3>
            <p className="text-xs text-slate-500 mb-4">
              Your account will be deactivated immediately and permanently deleted in 30 days. You have 30 days to change your mind by contacting support.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setShowDeleteStep(1)} className="flex-1 px-4 py-2.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition" data-testid="delete-goback-btn">Go Back</button>
              <button
                onClick={async () => {
                  try {
                    await axios.delete(`${API}/user/account`, { data: { reason: deleteReason || null } });
                    localStorage.clear();
                    logout();
                    toast.success("Your account has been scheduled for deletion. You have 30 days to contact support if you change your mind.");
                    navigate("/onboarding");
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Something went wrong");
                  }
                }}
                className="flex-1 px-4 py-2.5 text-xs font-bold text-white bg-red-600 rounded-xl hover:bg-red-700 transition"
                data-testid="delete-confirm-btn"
              >
                Delete My Account
              </button>
            </div>
          </div>
        </div>
      )}
      <BottomNav />
      <TooltipOverlay screen="badges" targetSelector='[data-testid="badge-chase-card"]' message="Your badge collection! Complete actions to earn new badges and level up." placement="top" />
    </div>
  );
}
